/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import React, { useContext, useEffect, useMemo, useState } from 'react'

import {
  ApprovalState,
  useCoordinator,
  useDebounce,
  UUID,
} from '@builddocs/data-management'
import {
  Certificate,
  Application,
  useDocumentary,
  Laboratory,
  WorkingDraft,
  Normative,
  ExecutiveScheme,
  ExecutiveDrawing,
  isWorkingDraft,
  isCertificate,
  Supporting,
  FILE_STORE_BASE,
  Permits,
  LoadableApplicationKeys,
  WorkingDraftPage,
} from '@builddocs/filestore-api'
import { CipherFilter } from '@builddocs/mui'
import { ViewerContext } from '@builddocs/shared/feature-pdf-viewer'
import { useDisclosure } from '@builddocs/system'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import RotateLeftOutlinedIcon from '@mui/icons-material/RotateLeftOutlined'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Dialog,
  Typography,
  Button,
  Checkbox,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  DialogContentText,
  IconButton,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  Table,
  Input,
  CircularProgress,
  Pagination,
  InputAdornment,
  TableContainer,
  CheckboxProps,
} from '@mui/material'
import format from 'date-fns/format'
import { ru } from 'date-fns/locale'
import parseISO from 'date-fns/parseISO'
import _ from 'lodash'

import EditTextField from './EditTextField'
import { isDocumentFieldDisabled } from './isDocumentFieldDisabled'
import ListChecker from './ListChecker'
import { RowPDFView } from './RowPDFView'

const tableForm = ({ ...props }: CheckboxProps) => ({
  [Application.LABORATORY]: {
    headers: [
      <Checkbox key="checkbox" {...props} />,
      'Документ',
      'Дата выдачи',
      'Дата окончания',
    ],
    columns: ['checkbox', 'name', 'issueDate', 'expirationDate'],
  },
  [Application.CERTIFICATE]: {
    headers: [
      <Checkbox key="checkbox" {...props} />,
      'Документ',
      'Дата выдачи',
      'Дата окончания',
    ],
    columns: ['checkbox', 'name', 'issueDate', 'expirationDate'],
  },
  [Application.WORKING_DRAFT]: {
    headers: [
      <Checkbox key="checkbox" {...props} />,
      'Документ',
      'Дата выдачи',
      'Шифр',
      'Лист',
    ],
    columns: ['checkbox', 'name', 'issueDate', 'cipher', 'sheet'],
  },
  [Application.EXECUTIVE_SCHEME]: {
    headers: [
      <Checkbox key="checkbox" {...props} />,
      'Документ',
      'Номер документа',
      'Лист',
      'Дата выдачи',
    ],
    columns: ['checkbox', 'name', 'cipher', 'sheet', 'issueDate'],
  },
  [Application.EXECUTIVE_DRAWING]: {
    headers: [
      <Checkbox key="checkbox" {...props} />,
      'Документ',
      'Шифр',
      'Лист',
      'Дата выдачи',
    ],
    columns: ['checkbox', 'name', 'cipher', 'sheet', 'issueDate'],
  },
  [Application.NORMATIVE]: {
    headers: [<Checkbox key="checkbox" {...props} />, 'Документ'],
    columns: ['checkbox', 'name'],
  },
  [Application.PERMIT]: {
    headers: [<Checkbox key="checkbox" {...props} />, 'Документ'],
    columns: ['checkbox', 'name'],
  },
  [Application.SUPPORTING]: {
    headers: [
      <Checkbox key="checkbox" {...props} />,
      'Документ',
      'Дата выдачи',
    ],
    columns: ['checkbox', 'name', 'issueDate'],
  },
})

const getType = (
  documentType: Application,
): { docType: string; action: string } => {
  switch (documentType) {
    case Application.LABORATORY:
      return { docType: `Лаборатории `, action: `Добавить лабораторию` }
    case Application.EXECUTIVE_SCHEME:
      return {
        docType: 'Исполнительные схемы: ',
        action: 'Добавить исполнительную схему',
      }
    case Application.EXECUTIVE_DRAWING:
      return {
        docType: 'Исполнительные чертежи: ',
        action: 'Добавить исполнительный чертёж',
      }
    case Application.NORMATIVE:
      return {
        docType: 'Нормативные документы: ',
        action: 'Добавить нормативный документ',
      }
    case Application.WORKING_DRAFT:
      return { docType: 'Проекты: ', action: 'Добавить проект' }
    case Application.CERTIFICATE:
      return {
        docType: 'Сертификаты: ',
        action: 'Добавить документ подтверждающий качество',
      }
    case Application.SUPPORTING:
      return {
        docType: 'Сопроводительные документы: ',
        action: 'Добавить сопроводительный документ',
      }
    case Application.PERMIT:
      return {
        docType: 'Разрешительная документы: ',
        action: 'Добавить разрешительный документ',
      }
    default:
      return { docType: documentType, action: documentType }
  }
}

type Props<T> = {
  actStatus?: ApprovalState
  title?: string
  documentType: LoadableApplicationKeys
  isSmallButton?: boolean
  isSingleChoice?: boolean
  initialDocs: T[]
  workType?: string
  laboratoriesName?: string
  attachedDocuments?: UUID[]
  isForceLoading?: boolean
  onChange?: (value: string, field: string, docId: UUID) => void
  fieldsForUpdate: string[]
  onSelect: (docs: T[]) => void
  workDate?: string
}

function PaperComponent(props) {
  return <Paper {...props} />
}

/**
 *Диалог выбора документов
 */

function SelectingDocuments<
  T extends
    | Laboratory
    | WorkingDraft
    | Certificate
    | Normative
    | ExecutiveScheme
    | ExecutiveDrawing
    | Supporting
    | Permits,
>(props: Props<T>) {
  const {
    actStatus,
    title,
    documentType,
    initialDocs,
    attachedDocuments,
    onChange,
    onSelect,
    fieldsForUpdate,
    workDate,
  } = props

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { projectId } = useCoordinator() as { projectId: string }
  const [initialDocsFreeze] = useState(initialDocs)
  const [selected, setSelected] = useState<T[]>(initialDocs)
  const [searchValue, setSearchValue] = useState<string>()
  const [pageNumber, setPageNumber] = useState<number>(0)
  const [selectedCipher, setSelectedCipher] = useState<string | undefined>('')

  const viewerContext = useContext(ViewerContext)

  const hasValue = useMemo(() => selected?.length > 0, [selected])

  const debouncedSearchTerm = useDebounce(searchValue, 500)

  const { docs, isLoading, pageCount, refreshDocs } = useDocumentary({
    documentType,
    pageNumber,
    projectId,
    searchValue: debouncedSearchTerm,
    cipher:
      (documentType === Application.EXECUTIVE_SCHEME && selectedCipher) ||
      undefined,
  })

  useEffect(() => {
    setSelected(initialDocs)
  }, [initialDocs])

  const handleChangePage = (_e, newPage) => {
    setPageNumber(newPage - 1)
  }

  const handleSelect = (doc: T) => {
    setSelected(prev =>
      prev.some(_doc => _doc.id === doc.id)
        ? prev.filter(_doc => _doc.id !== doc.id)
        : [...prev, { ...doc, documentType }],
    )
  }

  const handleClose = () => {
    setSelected(initialDocs)
    onClose()
  }

  const handleSave = () => {
    onSelect(selected)
    onClose()
  }

  const handleOpen = (event: React.MouseEvent) => {
    event.preventDefault()
    onOpen()
  }

  const handleSearch = (value: string) => {
    setSearchValue(value)
    setPageNumber(0)
  }

  const handleSelectCipher = (value: string) => {
    setSelectedCipher(value)
  }

  const allChecked = useMemo(() => {
    for (const doc of docs) {
      if (!selected.some(_doc => _doc.id === doc.id)) {
        return false
      }
    }
    return true
  }, [docs, selected])

  const someCheckedButNotAll =
    selected?.some(sel => docs?.some(d => d.id == sel.id)) && !allChecked

  const handleHeaderCheckbox = () => {
    if (allChecked) {
      setSelected(val => val.filter(v => !docs.some(d => d.id === v.id)))
      return
    }

    const selectedDocs = docs.reduce(
      // @ts-expect-error ошибка ts из-за разных аргументов с тем же constraint type
      (acc, val) => (!acc.some(s => s.id === val.id) ? [...acc, val] : acc),
      selected,
    ) as unknown as T[]

    setSelected(
      selectedDocs
        ?.filter(d => !attachedDocuments?.includes(d.id))
        ?.map(d => ({ ...d, documentType })),
    )
  }

  const componentType = getType(documentType)

  return (
    <>
      <Stack alignItems="flex-start" direction="row" mb={2}>
        {hasValue ? (
          <Stack>
            <Stack alignItems="center" direction="row" spacing={2}>
              <Typography color="text.secondary" variant="subtitle2">
                {componentType.docType}
              </Typography>
              <IconButton id="edit_list" onClick={onOpen}>
                <AddOutlinedIcon />
              </IconButton>
              {(initialDocsFreeze.length !== initialDocs.length ||
                _.differenceBy(initialDocsFreeze, initialDocs, doc => doc.id)
                  .length > 0) && (
                <Button
                  size="small"
                  onClick={() => {
                    setSelected(initialDocsFreeze)
                    onSelect(initialDocsFreeze)
                  }}
                >
                  Отменить
                </Button>
              )}
            </Stack>
            {onChange ? (
              <Table>
                <TableBody>
                  {selected.map(doc => {
                    return (
                      <TableRow key={doc.id}>
                        {fieldsForUpdate.map((field: string) => (
                          <TableCell key={doc.id + field}>
                            <EditTextField
                              disabled={isDocumentFieldDisabled({
                                actStatus,
                                documentType,
                                field,
                                // @ts-expect-error ошибка
                                modificationAllowed: doc.modificationAllowed,
                              })}
                              documentType={documentType}
                              field={field}
                              id={doc.id + field}
                              modificationAllowed={
                                // @ts-expect-error ошибка
                                doc.modificationAllowed &&
                                (actStatus === ApprovalState.NEW ||
                                  actStatus === ApprovalState.RECREATED)
                              }
                              value={doc[field]}
                              onSave={newValue =>
                                onChange(newValue, field, doc.id)
                              }
                            />
                          </TableCell>
                        ))}
                        <TableCell>
                          <IconButton
                            id={'show_file_' + doc.id}
                            onClick={() =>
                              viewerContext.setPDF({
                                url: `${FILE_STORE_BASE}${doc.documentUrl}`,
                                fileName: doc.fileName,
                                fileId: doc.id,
                              })
                            }
                          >
                            <PictureAsPdfOutlinedIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            ) : (
              selected.map(doc => (
                <RowPDFView
                  key={doc.id}
                  id={doc.id}
                  link={doc.documentUrl ?? ''}
                  title={`${doc.name}  ${
                    'number' in doc ? doc.number : 'б/н'
                  } от ${
                    doc.issueDate
                      ? format(parseISO(doc.issueDate), 'PP', {
                          locale: ru,
                        })
                      : 'б/д'
                  }`}
                />
              ))
            )}
          </Stack>
        ) : (
          <Button onClick={handleOpen}>{title ?? componentType.action}</Button>
        )}
      </Stack>

      <Dialog
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        id="assignees-dialog-modal"
        maxWidth="lg"
        open={isOpen}
        onClose={handleClose}
      >
        <Stack direction="row">
          <Box
            sx={{
              flex: '1 1 auto',
              borderRight: '1px dashed rgb(224, 224, 224)',
            }}
          >
            <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
              {componentType.action}
            </DialogTitle>
            <DialogContent>
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                mb={2}
                mt={1}
                spacing={2}
              >
                <DialogContentText>
                  Список документов, загруженных в Документарий
                </DialogContentText>

                <Stack alignItems="center" direction="row" spacing={2}>
                  <Input
                    endAdornment={
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    }
                    id="search-docs-input"
                    placeholder="Поиск"
                    type="search"
                    onChange={e => handleSearch(e.target.value)}
                  />
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <IconButton id="reload_docs_button" onClick={refreshDocs}>
                      <RotateLeftOutlinedIcon />
                    </IconButton>
                  )}
                  {documentType === Application.EXECUTIVE_SCHEME && (
                    <Box width="300px">
                      <CipherFilter
                        documentTypeTab={documentType}
                        initialValue={
                          selectedCipher === '' ? 'Все' : selectedCipher
                        }
                        projectId={projectId}
                        onSelect={handleSelectCipher}
                      />
                    </Box>
                  )}
                </Stack>
              </Stack>
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table
                  stickyHeader
                  aria-label="sticky table"
                  aria-labelledby="tableTitle"
                  size="small"
                >
                  <TableHead>
                    <TableRow>
                      {tableForm({
                        onChange: handleHeaderCheckbox,
                        checked: allChecked,
                        indeterminate: someCheckedButNotAll,
                        size: 'small',
                      })[documentType].headers.map((th, index) => (
                        <TableCell
                          key={th}
                          padding={index === 0 ? 'checkbox' : undefined}
                        >
                          {th}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {docs?.map(doc => (
                      <TableRow key={doc.id}>
                        {tableForm({})[documentType].columns.map(
                          (field: string) => {
                            switch (field) {
                              case 'checkbox':
                                return (
                                  <TableCell key={field} padding="checkbox">
                                    {(
                                      'expirationDate' in doc &&
                                      doc.expirationDate &&
                                      workDate
                                        ? new Date(doc.expirationDate) <=
                                          new Date(workDate)
                                        : false
                                    ) ? null : (
                                      <Checkbox
                                        checked={
                                          selected &&
                                          selected.some(
                                            item =>
                                              item.id === doc.id ||
                                              item.documentId === doc.id,
                                          )
                                        }
                                        size="small"
                                        onChange={() => handleSelect(doc as T)}
                                      />
                                    )}
                                  </TableCell>
                                )
                              case 'name':
                                return (
                                  <TableCell key={field}>
                                    <Stack>
                                      <Stack
                                        alignItems="center"
                                        direction="row"
                                      >
                                        <Typography
                                          sx={{
                                            wordBreak: 'break-all',
                                          }}
                                          variant="subtitle2"
                                        >
                                          {doc.name}
                                        </Typography>
                                        <IconButton
                                          id={'show_file_' + doc.id}
                                          onClick={() =>
                                            viewerContext.setPDF({
                                              url: `${FILE_STORE_BASE}${doc.documentUrl}`,
                                              fileName: doc.fileName,
                                              fileId: doc.id,
                                            })
                                          }
                                        >
                                          <PictureAsPdfOutlinedIcon />
                                        </IconButton>
                                      </Stack>
                                      {'number' in doc && doc.number && (
                                        <Typography
                                          color="text.secondary"
                                          variant="subtitle2"
                                        >
                                          {doc.number}
                                        </Typography>
                                      )}
                                      {'description' in doc &&
                                      doc.description ? (
                                        <Typography
                                          color="text.secondary"
                                          variant="subtitle2"
                                        >
                                          {doc.description}
                                        </Typography>
                                      ) : null}
                                      {isWorkingDraft({
                                        ...doc,
                                        documentType:
                                          documentType as Application,
                                      }) && (
                                        <Box>
                                          <Typography
                                            color="text.secondary"
                                            variant="subtitle2"
                                          >
                                            Шифр:{' '}
                                            {'cipher' in doc && doc.cipher}
                                          </Typography>
                                          <ListChecker
                                            doc={doc as WorkingDraft}
                                            id={`${doc.id}-list-checker`}
                                            selected={
                                              selected &&
                                              selected.reduce(
                                                (acc, doc) => ({
                                                  ...acc,
                                                  [doc.id]: doc,
                                                }),
                                                {},
                                              )
                                            }
                                            onCheck={
                                              handleSelect as (
                                                doc: WorkingDraftPage,
                                              ) => void
                                            }
                                            onUpdateDoc={freshDocs =>
                                              setSelected(
                                                prev =>
                                                  prev.map(
                                                    doc =>
                                                      freshDocs.find(
                                                        _doc =>
                                                          _doc.id === doc.id,
                                                      ) || doc,
                                                  ) as T[],
                                              )
                                            }
                                          />
                                        </Box>
                                      )}
                                      {isCertificate(doc) && (
                                        <Typography variant="subtitle2">
                                          Производитель:{' '}
                                          {doc.manufacturer || 'не указан'}
                                        </Typography>
                                      )}
                                      {attachedDocuments?.includes(doc.id) && (
                                        <Typography
                                          color="grey"
                                          variant="subtitle2"
                                        >
                                          Используется в материале
                                        </Typography>
                                      )}
                                    </Stack>
                                  </TableCell>
                                )
                              case 'issueDate':
                                return (
                                  <TableCell key={field}>
                                    <Stack direction="column-reverse">
                                      {doc.issueDate
                                        ? format(
                                            parseISO(doc.issueDate),
                                            'dd MMM yyyy г.',
                                            {
                                              locale: ru,
                                            },
                                          )
                                        : '-'}
                                      <Typography
                                        color="text.secondary"
                                        variant="subtitle2"
                                      >
                                        Организация:
                                      </Typography>
                                      <Typography variant="subtitle2">
                                        {doc.organization ?? ''}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                )
                              case 'expirationDate':
                                return (
                                  <TableCell key={field}>
                                    {'expirationDate' in doc &&
                                    doc.expirationDate
                                      ? format(
                                          parseISO(doc.expirationDate),
                                          'dd MMM yyyy г.',
                                          {
                                            locale: ru,
                                          },
                                        )
                                      : '-'}
                                  </TableCell>
                                )
                              default:
                                return (
                                  <TableCell key={field}>
                                    {doc[field]}
                                  </TableCell>
                                )
                            }
                          },
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack alignItems="end" mt={2}>
                <Pagination
                  count={pageCount}
                  page={pageNumber + 1}
                  size="small"
                  onChange={handleChangePage}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button autoFocus onClick={handleClose}>
                Отмена
              </Button>
              <Button size="small" variant="contained" onClick={handleSave}>
                Сохранить
              </Button>
            </DialogActions>
          </Box>
          <Box
            sx={{
              width: '400px',
              flex: '0 0 400px',
            }}
          >
            <DialogTitle>Выбранные документы</DialogTitle>
            <DialogContent>
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                mb={2}
                mt={1}
                spacing={2}
                sx={{
                  visibility: 'hidden',
                  height: '37px',
                }}
              ></Stack>
              {!!selected?.length && (
                <TableContainer sx={{ maxHeight: 440 }}>
                  <Table
                    stickyHeader
                    aria-label="sticky table"
                    aria-labelledby="tableTitle"
                    size="small"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          {
                            tableForm({
                              onChange: () => setSelected([]),
                              checked: true,
                              size: 'small',
                            })[documentType].headers?.[0]
                          }
                        </TableCell>
                        <TableCell>
                          {tableForm({})[documentType].headers?.[1]}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selected?.map(doc => (
                        <TableRow key={doc.id}>
                          <TableCell padding="checkbox">
                            {(
                              'expirationDate' in doc &&
                              doc.expirationDate &&
                              workDate
                                ? new Date(doc.expirationDate) <=
                                  new Date(workDate)
                                : false
                            ) ? null : (
                              <Checkbox
                                checked={true}
                                size="small"
                                onChange={() => handleSelect(doc as T)}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack>
                              <Typography
                                sx={{
                                  wordBreak: 'break-all',
                                }}
                                variant="subtitle2"
                              >
                                {doc.name}
                              </Typography>
                              {'number' in doc && doc.number && (
                                <Typography
                                  color="text.secondary"
                                  variant="subtitle2"
                                >
                                  {doc.number}
                                </Typography>
                              )}
                              {'description' in doc && doc.description ? (
                                <Typography
                                  color="text.secondary"
                                  variant="subtitle2"
                                >
                                  {doc.description}
                                </Typography>
                              ) : null}
                              {isWorkingDraft({
                                ...doc,
                                documentType: documentType as Application,
                              }) && (
                                <Box>
                                  <Typography
                                    color="text.secondary"
                                    variant="subtitle2"
                                  >
                                    Шифр: {'cipher' in doc && doc.cipher}
                                  </Typography>
                                </Box>
                              )}
                              {isCertificate(doc) && (
                                <Typography variant="subtitle2">
                                  Производитель:{' '}
                                  {doc.manufacturer || 'не указан'}
                                </Typography>
                              )}
                              {attachedDocuments?.includes(doc.id) && (
                                <Typography color="grey" variant="subtitle2">
                                  Используется в материале
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions></DialogActions>
          </Box>
        </Stack>
      </Dialog>
    </>
  )
}

export default SelectingDocuments
